import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Kanban } from '@dhx/kanban';
// Bypasses postcss-loader via raw-loader so the dhtmlxKanban CSS isn't processed
// through postcss-preset-env, which rejects certain CSS custom-property calc()
// expressions in the vendor file with a lexer error. The CSS is injected once
// into <head> at runtime instead.
// eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved
import kanbanCssRaw from '!!raw-loader!@dhx/kanban/dist/kanban.css';

function injectKanbanStyles() {
    const id = 'dhx-kanban-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = typeof kanbanCssRaw === 'string' ? kanbanCssRaw : kanbanCssRaw.default;
    document.head.appendChild(el);
}

// Maps common priority label strings (case-insensitive) to hex colors used for
// the card's top stripe (card.color) and the priority badge (cardShape.priority.values).
// Unknown values fall back to PRIORITY_COLOR_DEFAULT so the card still renders.
const PRIORITY_COLORS = {
    high: '#EF4444',
    highest: '#EF4444',
    critical: '#EF4444',
    urgent: '#EF4444',
    p1: '#EF4444',
    '1': '#EF4444',
    medium: '#F97316',
    normal: '#F97316',
    moderate: '#F97316',
    p2: '#F97316',
    '2': '#F97316',
    low: '#22C55E',
    lowest: '#22C55E',
    minor: '#22C55E',
    trivial: '#22C55E',
    p3: '#22C55E',
    '3': '#22C55E',
};
const PRIORITY_COLOR_DEFAULT = '#94A3B8';

// Static list of all priority values for cardShape.priority.values.
// dhtmlxKanban matches card.priority (normalized string id) against this list
// to render the colored priority badge. Must live in cardShape, NOT at top level.
const ALL_PRIORITY_VALUES = [
    { id: 'high', color: '#EF4444', label: 'High' },
    { id: 'highest', color: '#EF4444', label: 'Highest' },
    { id: 'critical', color: '#EF4444', label: 'Critical' },
    { id: 'urgent', color: '#EF4444', label: 'Urgent' },
    { id: 'p1', color: '#EF4444', label: 'P1' },
    { id: '1', color: '#EF4444', label: '1' },
    { id: 'medium', color: '#F97316', label: 'Medium' },
    { id: 'normal', color: '#F97316', label: 'Normal' },
    { id: 'moderate', color: '#F97316', label: 'Moderate' },
    { id: 'p2', color: '#F97316', label: 'P2' },
    { id: '2', color: '#F97316', label: '2' },
    { id: 'low', color: '#22C55E', label: 'Low' },
    { id: 'lowest', color: '#22C55E', label: 'Lowest' },
    { id: 'minor', color: '#22C55E', label: 'Minor' },
    { id: 'trivial', color: '#22C55E', label: 'Trivial' },
    { id: 'p3', color: '#22C55E', label: 'P3' },
    { id: '3', color: '#22C55E', label: '3' },
];

function priorityId(rawValue) {
    if (rawValue == null) {
        return null;
    }
    return String(rawValue)
        .toLowerCase()
        .trim();
}

function priorityColor(id) {
    return id ? (PRIORITY_COLORS[id] || PRIORITY_COLOR_DEFAULT) : null;
}

// Builds the cardShape config with the current unique-users list.
// Called at init and on each data refresh so cardShape.users.values stays in
// sync with the assignees actually present on the board.
function buildCardShape(users) {
    return {
        label: { show: true },
        description: { show: true },
        progress: { show: false },
        users: {
            show: true,
            // IUser[] { id, label } — dhtmlx matches card.users (ID array) against this list
            values: users || [],
        },
        priority: {
            show: true,
            // Priority badge values; card.priority must equal one of these ids
            values: ALL_PRIORITY_VALUES,
        },
        end_date: { show: true },
        color: { show: true },
        cover: { show: false },
        menu: { show: false },
        // headerFields renders arbitrary card properties in the card header area
        headerFields: [ { key: 'labelsDisplay', css: 'dhx-card-labels' } ],
    };
}

/**
 * Converts the Salt column/card model into dhtmlxKanban's data shape.
 * Returns { columns, cards, rows, users } where users is the deduplicated
 * assignee list needed by cardShape.users.values.
 */
function toDhxData(columns, rows) {
    const dhxColumns = [];
    const dhxCards = [];
    const usersMap = new Map(); // assignee string → IUser { id, label }

    (columns || []).forEach(({ status, cards }) => {
        dhxColumns.push({ id: status, label: status });

        (cards || []).forEach(({ id, title, description, priority, progress, assignee, dueDate, swimlane, labels }) => {
            const pid = priorityId(priority);
            const pColor = priorityColor(pid);

            if (assignee != null && assignee !== '' && !usersMap.has(String(assignee))) {
                usersMap.set(String(assignee), { id: String(assignee), label: String(assignee) });
            }

            const card = {
                id,
                label: title || String(id),
                description: description || '',
                column: status,
            };

            if (pid) {
                card.priority = pid;
                card.color = pColor;
            }
            if (progress != null && !Number.isNaN(Number(progress))) {
                card.progress = Math.round(Number(progress));
            }
            if (assignee != null && assignee !== '') {
                // ICard.users expects TID[] (IDs only); display objects live in cardShape.users.values
                card.users = [ String(assignee) ];
            }
            if (dueDate != null && dueDate !== '') {
                const d = new Date(String(dueDate));
                card.end_date = Number.isNaN(d.getTime()) ? String(dueDate) : d;
            }
            if (swimlane != null && swimlane !== '') {
                card.row = String(swimlane);
            }
            if (Array.isArray(labels) && labels.length > 0) {
                card.labelsDisplay = labels.join(' · ');
            }

            dhxCards.push(card);
        });
    });

    const dhxRows = (rows || []).map((r) => (typeof r === 'object' ? r : { id: String(r), label: String(r) }));
    const dhxUsers = [ ...usersMap.values() ];

    return {
        columns: dhxColumns,
        cards: dhxCards,
        rows: dhxRows,
        users: dhxUsers,
    };
}

export default function RWKanbanBoard({ columns, rows, canDragCards, onCardPress, onCardDrop, style }) {
    const containerRef = useRef(null);
    const kanbanRef = useRef(null);

    // Stable ref so event handlers always call the latest callbacks without re-subscribing.
    const callbacksRef = useRef({ onCardPress, onCardDrop });
    callbacksRef.current = { onCardPress, onCardDrop };

    // id→card lookup; dhtmlx events surface only the card id, not the full object.
    const cardMapRef = useRef({});

    // Always-current columns/rows props; used inside event handlers (closure would be stale).
    const columnsRef = useRef(columns);
    columnsRef.current = columns;
    const rowsRef = useRef(rows);
    rowsRef.current = rows;

    // When true, a status-update save is in flight — suppress widget.parse so the
    // optimistic card position is preserved during the API round-trip.
    const savingRef = useRef(false);

    // Snapshot of columns before the drag so we can roll back on save failure.
    const preDropColumnsRef = useRef(columns);

    // Keep card lookup map in sync, but not while an optimistic save is in flight.
    useEffect(() => {
        if (savingRef.current) return;
        const map = {};
        (columns || []).forEach(({ cards }) => {
            (cards || []).forEach((card) => { map[String(card.id)] = card; });
        });
        cardMapRef.current = map;
    }, [ columns ]);

    // Init once on mount; destroyed on unmount.
    useEffect(() => {
        injectKanbanStyles();
        const data = toDhxData(columnsRef.current, rowsRef.current);

        const widget = new Kanban(containerRef.current, {
            columns: data.columns,
            cards: data.cards,
            rows: data.rows,
            readonly: { add: false, edit: false, dnd: canDragCards },
            theme: { name: 'material' },
            cardShape: buildCardShape(data.users),
        });

        widget.api.on('move-card', ({ id, columnId }) => {
            preDropColumnsRef.current = columnsRef.current;
            savingRef.current = true;

            Promise.resolve(callbacksRef.current.onCardDrop(String(id), String(columnId)))
                .then(() => { savingRef.current = false; })
                .catch(() => {
                    savingRef.current = false;
                    const w = kanbanRef.current;
                    if (w) {
                        const rollback = toDhxData(preDropColumnsRef.current, rowsRef.current);
                        w.setConfig({ cardShape: buildCardShape(rollback.users) });
                        w.parse(rollback);
                    }
                });
        });

        widget.api.on('select-card', ({ id }) => {
            const card = cardMapRef.current[String(id)];
            if (card) callbacksRef.current.onCardPress(card);
        });

        kanbanRef.current = widget;
        return () => widget.destructor();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // intentionally init-once

    // Sync board data on column/card/row changes — skip while an optimistic save is in flight.
    // setConfig refreshes cardShape.users.values with any newly seen assignees before parse.
    useEffect(() => {
        if (savingRef.current) return;
        const widget = kanbanRef.current;
        if (!widget) return;
        const data = toDhxData(columns, rows);
        widget.setConfig({ cardShape: buildCardShape(data.users) });
        widget.parse(data);
    }, [
        columns,
        rows,
    ]);

    // Reflect drag-permission changes live without remounting.
    useEffect(() => {
        const widget = kanbanRef.current;
        if (!widget) return;
        widget.setConfig({ readonly: { add: false, edit: false, dnd: canDragCards } });
    }, [ canDragCards ]);

    return (
        <div
            ref={ containerRef }
            style={ { height: '100%', width: '100%', flexGrow: 1, ...style } } />
    );
}

RWKanbanBoard.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        status: PropTypes.string,
        cards: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.number,
            ]),
            title: PropTypes.string,
            description: PropTypes.string,
            priority: PropTypes.string,
            progress: PropTypes.number,
            assignee: PropTypes.string,
            dueDate: PropTypes.string,
            swimlane: PropTypes.string,
            labels: PropTypes.arrayOf(PropTypes.string),
        })),
    })),
    rows: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({ id: PropTypes.string, label: PropTypes.string }),
    ])),
    canDragCards: PropTypes.bool,
    onCardPress: PropTypes.func,
    onCardDrop: PropTypes.func,
    style: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
    ]),
};

RWKanbanBoard.defaultProps = {
    columns: [],
    rows: [],
    canDragCards: false,
    onCardPress: () => {},
    onCardDrop: () => {},
    style: null,
};
